import { ModuleLayout } from '@billy/design-system'
import React, { ReactElement } from 'react'
import { useLocation } from 'react-router'

import { BillType } from '@features/bills/enums/billType'
import { useGetBill } from '@features/bills/hooks/useGetBill'
import { BankIntegrationContextProvider } from '@features/openBanking/contexts/bankIntegrationContext'
import { BankConnectionStatusActions } from '@features/openBanking/elements/BankConnectionStatusActions'
import { ContactBalanceContextProvider } from '@features/paymentReconciliationModal/contexts'
import { TransactionViewProvider } from '@features/transactions/contexts/transactionViewContext'
import { TransactionViewTrackingContext } from '@features/transactions/enums/transactionViewTrackingContext'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { Scope } from '../../../../enums/scope'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { BillAttachmentPageContextProvider } from '../../contexts/billAttachmentPageContext'
import { BillReadableActions } from '../BillReadableActions'
import { BillViewContextProvider } from '../BillReadableForm'
import { BillReadableLayout } from '../BillReadableLayout'
import { BillReadableTitle } from '../BillReadableTitle'

interface BillReadableProps {
  billId: string
}

export const BillReadable = ({ billId }: BillReadableProps): ReactElement => {
  const location = useLocation()
  const { url: billsUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getMainRoute())
  const { organization } = useUserOrganization()
  const { data: billData } = useGetBill(billId)

  const showBillActions = isAuthorized(Scope.BillWrite)

  return (
    <BillAttachmentPageContextProvider>
      <BillViewContextProvider billId={billId}>
        <BankIntegrationContextProvider organizationId={organization?.id as string}>
          <BankConnectionStatusActions />
          <ContactBalanceContextProvider
            contactId={billData?.bill?.contactId || undefined}
            currencyId={billData?.bill?.currencyId || undefined}
            subjectType={BillType.Bill}
          >
            <TransactionViewProvider trackingContext={TransactionViewTrackingContext.Bill}>
              <ModuleLayout
                fullHeight
                backButtonTo={billsUrl && `${billsUrl}${location.search}`}
                sideActions={showBillActions ? <BillReadableActions /> : null}
                title={<BillReadableTitle />}
                withBackButton
              >
                <BillReadableLayout />
              </ModuleLayout>
            </TransactionViewProvider>
          </ContactBalanceContextProvider>
        </BankIntegrationContextProvider>
      </BillViewContextProvider>
    </BillAttachmentPageContextProvider>
  )
}
